import { Link } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";
import { Trash2, Sparkles, AlertTriangle, X, Mail, UserCog, Send, CheckCircle2, Search } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const statuses = ["new", "payment_pending", "paid", "processing", "packed", "shipped", "delivered", "cancelled", "refunded"];

const RECOVERABLE_STATUSES = new Set(["pending", "authorising", "payment_pending", "failed"]);
const fmtStamp = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

/** Render an ISO timestamp (or Date) in UK format: `21 Feb 2026 · 14:32`.
 * Falls back to `—` if the input is empty/invalid. Locale forced to en-GB
 * + Europe/London so admin sees the same value Royal Mail / HMRC would. */
function formatUkDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const datePart = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "Europe/London" });
  const timePart = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/London" });
  return `${datePart} · ${timePart}`;
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [reminderBusy, setReminderBusy] = useState(null);
  const [stopBusy, setStopBusy] = useState(null);

  const toggleRecovery = async (order) => {
    const stopping = !order.recovery_stopped;
    let reason = null;
    if (stopping) {
      reason = window.prompt(
        `Stop automatic payment reminders for ${order.order_number} (${order.shipping?.email})?\n\nOptional note (e.g. "personal email sent"):`, "");
      if (reason === null) return;
    }
    setStopBusy(order.id);
    try {
      await api.post(`/admin/orders/${order.id}/stop-recovery`, { stopped: stopping, reason });
      toast.success(stopping ? "Automatic reminders stopped for this order" : "Automatic reminders resumed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setStopBusy(null);
    }
  };
  const [resendBusy, setResendBusy] = useState(null);
  const [markPaidBusy, setMarkPaidBusy] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);

  const loadOlder = async () => {
    setLoadingOlder(true);
    try {
      const statusQ = filter === "all" ? "" : `&status=${filter}`;
      const { data } = await api.get(`/admin/orders?limit=500&skip=${orders.length}${statusQ}`);
      if (data.length < 500) setAllLoaded(true);
      setOrders((prev) => {
        const seen = new Set(prev.map((o) => o.id));
        return [...prev, ...data.filter((o) => !seen.has(o.id))];
      });
    } finally {
      setLoadingOlder(false);
    }
  };

  const load = () => {
    const url = filter === "all" ? "/admin/orders" : `/admin/orders?status=${filter}`;
    api.get(url).then((r) => setOrders(r.data));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  // Server-side search across the ENTIRE order history (beyond the 500 most
  // recent loaded by default). Debounced; merges results into local state.
  useEffect(() => {
    const s = search.trim();
    if (s.length < 3) return;
    const t = setTimeout(() => {
      api.get(`/admin/orders?search=${encodeURIComponent(s)}&limit=1000`).then((r) => {
        setOrders((prev) => {
          const seen = new Set(prev.map((o) => o.id));
          const extra = r.data.filter((o) => !seen.has(o.id));
          return extra.length ? [...prev, ...extra] : prev;
        });
      }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // Client-side filter — instant, no API round-trip. Matches against
  // order_number, customer name, email, postcode, phone and discount code
  // so admins can paste any of these from a customer email and find the order.
  const filteredOrders = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return orders;
    return orders.filter((o) => {
      const sh = o.shipping || {};
      return (
        (o.order_number || "").toLowerCase().includes(s) ||
        (sh.full_name || "").toLowerCase().includes(s) ||
        (sh.email || "").toLowerCase().includes(s) ||
        (sh.postcode || "").toLowerCase().includes(s) ||
        (sh.phone || "").toLowerCase().includes(s) ||
        (sh.city || "").toLowerCase().includes(s) ||
        (o.discount_code || "").toLowerCase().includes(s) ||
        (o.tracking_number || "").toLowerCase().includes(s)
      );
    });
  }, [orders, search]);

  const update = async (id, data) => {
    try {
      await api.patch(`/admin/orders/${id}`, data);
      toast.success("Order updated");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const resendConfirmation = async (order) => {
    if (!order.shipping?.email) {
      toast.error("Order has no email — fix that first");
      return;
    }
    if (!window.confirm(
      `Resend the order confirmation email to:\n\n` +
      `${order.shipping.email}\n\n` +
      `Order: ${order.order_number}  £${Number(order.total).toFixed(2)}`
    )) return;
    setResendBusy(order.id);
    try {
      const r = await api.post(`/admin/orders/${order.id}/resend-confirmation`);
      toast.success(`Confirmation email resent to ${r.data?.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally {
      setResendBusy(null);
    }
  };

  const resendDispatch = async (order) => {
    if (!order.shipping?.email) {
      toast.error("Order has no email — fix that first");
      return;
    }
    if (!window.confirm(
      `Resend the dispatch / tracking email to:\n\n` +
      `${order.shipping.email}\n\n` +
      `Order: ${order.order_number}\n` +
      `Tracking: ${order.tracking_number || "—"}`
    )) return;
    setResendBusy(order.id);
    try {
      const r = await api.post(`/admin/orders/${order.id}/resend-dispatch`);
      toast.success(`Dispatch email resent to ${r.data?.sent_to}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend dispatch failed");
    } finally {
      setResendBusy(null);
    }
  };

  const markAsPaid = async (order) => {
    if (!window.confirm(
      `Mark this order as PAID?\n\n` +
      `Order: ${order.order_number}  £${Number(order.total).toFixed(2)}\n` +
      `Customer: ${order.shipping?.full_name} <${order.shipping?.email}>\n\n` +
      `Use this AFTER you've confirmed the bank transfer landed in Tide.\n\n` +
      `This will:\n` +
      `  • Flip payment_status to "paid"\n` +
      `  • Send the order confirmation email to the customer\n` +
      `  • Fire Meta CAPI Purchase event (preserves ad attribution)\n` +
      `  • Set payment_timestamp\n` +
      `  • Lock in stock reservation\n\n` +
      `This cannot be undone — refund via "Update Status → refunded" if needed.`
    )) return;
    setMarkPaidBusy(order.id);
    try {
      await api.patch(`/admin/orders/${order.id}`, { payment_status: "paid" });
      toast.success(`${order.order_number} marked paid — email + Meta CAPI fired`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mark paid failed");
    } finally {
      setMarkPaidBusy(null);
    }
  };

  const sendPaymentReminder = async (order) => {
    const nextStage = !order.recovery_first_sent_at
      ? "1st nudge — 'Your basket is held'"
      : !order.recovery_second_sent_at
        ? "2nd nudge — 'Need help with Pay-by-Bank?'"
        : !order.recovery_final_sent_at
          ? "3rd nudge — '5% off, last chance'"
          : null;
    if (!nextStage) {
      toast.error("All 3 reminder stages already sent for this order");
      return;
    }
    if (!window.confirm(
      `Send payment-reminder email to ${order.shipping?.email}?\n\n` +
      `Order: ${order.order_number}  £${Number(order.total).toFixed(2)}\n` +
      `Stage: ${nextStage}`
    )) return;
    setReminderBusy(order.id);
    try {
      const r = await api.post(`/admin/orders/${order.id}/send-payment-reminder`);
      const stage = r.data?.stage || "reminder";
      const code = r.data?.discount_code ? ` (code: ${r.data.discount_code})` : "";
      toast.success(`${stage} reminder sent to ${r.data?.sent_to}${code}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setReminderBusy(null);
    }
  };

  const remove = async (order) => {
    const isPaid = ["paid", "processing", "packed", "shipped", "delivered"].includes(order.status);
    const warn = isPaid
      ? `\n\n⚠️ This order has a status of "${order.status}" — deleting a paid/dispatched order may impact your records and the customer's expectation of delivery. Continue?`
      : "";
    if (!window.confirm(
      `Permanently delete order ${order.order_number}?\n\n` +
      `Customer: ${order.shipping?.full_name}\n` +
      `Email: ${order.shipping?.email}\n` +
      `Total: £${Number(order.total).toFixed(2)}\n\n` +
      `This cannot be undone.${warn}`
    )) return;
    try {
      await api.delete(`/admin/orders/${order.id}`);
      toast.success(`Order ${order.order_number} deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const openCleanupPreview = async () => {
    setCleanupBusy(true);
    try {
      const { data } = await api.get("/admin/orders/cleanup-test/preview");
      setCleanupPreview(data);
    } catch (e) {
      toast.error("Could not load cleanup preview");
    } finally {
      setCleanupBusy(false);
    }
  };

  const confirmCleanup = async () => {
    setCleanupBusy(true);
    try {
      const { data } = await api.post("/admin/orders/cleanup-test");
      toast.success(`Deleted ${data.deleted} test order${data.deleted === 1 ? "" : "s"}`);
      setCleanupPreview(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleanupBusy(false);
    }
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="pg-micro mb-2">// Orders</div>
          <h1 className="text-[#F7FAFC] text-3xl pg-display" style={{ fontFamily: "'Exo 2'" }}>Order Management</h1>
        </div>
        <button
          type="button"
          onClick={openCleanupPreview}
          disabled={cleanupBusy}
          className="text-[10px] font-bold tracking-[0.18em] uppercase rounded-sm py-2 px-3 border border-[#FF8A5B]/40 text-[#FF8A5B] hover:bg-[#FF8A5B]/10 transition-colors flex items-center gap-2 disabled:opacity-50"
          data-testid="cleanup-test-orders-btn"
        >
          <Sparkles size={12} /> Clean up test orders
        </button>
      </div>

      <div className="mb-4">
        <div className="relative max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA8B8]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by order #, name, email, postcode, phone or code…"
            className="pg-input pl-9 w-full text-sm"
            data-testid="admin-orders-search"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA8B8] hover:text-[#F7FAFC]"
              data-testid="admin-orders-search-clear"
              type="button"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {search && (
          <div className="mt-2 text-[11px] pg-mono text-[#9CA8B8] uppercase tracking-[0.18em]" data-testid="admin-orders-search-count">
            {filteredOrders.length} of {orders.length} order{orders.length === 1 ? "" : "s"} match &quot;{search}&quot;
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {["all", ...statuses].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-[10px] pg-mono uppercase tracking-[0.2em] border rounded-sm ${
              filter === s ? "border-[#00C2D6] text-[#00C2D6] bg-[#00C2D6]/10" : "border-[#00C2D6]/20 text-[#9CA8B8]"
            }`}
            data-testid={`order-filter-${s}`}
          >
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filteredOrders.length === 0 && search && (
          <div className="pg-card p-6 text-center text-[#9CA8B8] text-sm" data-testid="admin-orders-no-match">
            No orders match &quot;<span className="text-[#F7FAFC] pg-mono">{search}</span>&quot;. Try a different keyword or clear the search.
          </div>
        )}
        {filteredOrders.map((o) => (
          <div key={o.id} className="pg-card p-5" data-testid={`order-${o.order_number}`}>
            <button onClick={() => setExpanded(expanded === o.id ? null : o.id)} className="w-full text-left">
              <div className="grid grid-cols-2 md:grid-cols-7 gap-4 items-center">
                <div>
                  <div className="pg-micro mb-1">Order</div>
                  <div className="text-[#F7FAFC] pg-mono text-sm">{o.order_number}</div>
                </div>
                <div data-testid={`order-date-${o.order_number}`}>
                  <div className="pg-micro mb-1">Placed</div>
                  <div className="text-[#F7FAFC] pg-mono text-[11px] leading-tight" title={o.created_at || ""}>
                    {formatUkDateTime(o.created_at)}
                  </div>
                  {o.payment_timestamp && o.payment_timestamp !== o.created_at && (
                    <div className="text-[#A6FF00] pg-mono text-[10px] leading-tight mt-0.5" title={`Paid: ${o.payment_timestamp}`}>
                      Paid {formatUkDateTime(o.payment_timestamp)}
                    </div>
                  )}
                </div>
                <div>
                  <div className="pg-micro mb-1">Customer</div>
                  <div className="text-[#F7FAFC] text-sm flex items-center gap-2 flex-wrap">
                    <span className="truncate">{o.shipping.full_name}</span>
                    <ReturningChip
                      paid={o.customer_order_count || 0}
                      total={o.customer_total_orders || 0}
                    />
                  </div>
                </div>
                <div>
                  <div className="pg-micro mb-1">Total</div>
                  <div className="text-[#A6FF00] pg-mono">£{o.total.toFixed(2)}</div>
                  {o.discount_code && (
                    <div
                      className="mt-1 inline-flex items-center gap-1.5 px-1.5 py-0.5 border border-[#A6FF00]/40 rounded-sm bg-[#A6FF00]/10"
                      data-testid={`order-discount-chip-${o.order_number}`}
                      title={`Customer applied ${o.discount_code} — saved £${Number(o.discount || 0).toFixed(2)}`}
                    >
                      <span className="pg-mono text-[10px] uppercase tracking-[0.12em] text-[#A6FF00] font-semibold">
                        {o.discount_code}
                      </span>
                      {Number(o.discount) > 0 && (
                        <span className="pg-mono text-[10px] text-[#A6FF00]">
                          −£{Number(o.discount).toFixed(2)}
                        </span>
                      )}
                    </div>
                  )}
                  {o.referring_affiliate_slug && (
                    <Link
                      to="/admin/affiliates"
                      className="mt-1 ml-1 inline-flex items-center gap-1.5 px-1.5 py-0.5 border border-[#00C2D6]/40 rounded-sm bg-[#00C2D6]/10 hover:bg-[#00C2D6]/20"
                      data-testid={`order-affiliate-chip-${o.order_number}`}
                      title={`Referred by affiliate ${o.referring_affiliate_slug} — commission ${o.affiliate_commission_voided ? "voided" : o.payout_status === "paid" ? "paid out" : o.payout_status === "pending_payment" ? "requested" : "in pot if first order"} · open ledger`}
                    >
                      <span className="pg-mono text-[10px] uppercase tracking-[0.12em] text-[#00C2D6] font-semibold">
                        AFF {o.referring_affiliate_slug}
                      </span>
                      <span className="pg-mono text-[10px] text-[#00C2D6]">
                        {o.affiliate_commission_voided ? "void" : o.payout_status === "paid" ? "paid" : o.payout_status === "pending_payment" ? "req" : `£${(Math.max(0, Number(o.subtotal || 0) - Number(o.discount || 0)) * 0.1).toFixed(2)}`}
                      </span>
                    </Link>
                  )}
                </div>
                <div>
                  <div className="pg-micro mb-1">Payment</div>
                  <div className="text-[#F7FAFC] text-xs">{o.payment_status}</div>
                  {o.payment_method === "GlobalPay Card" ? (
                    <div className="mt-1 inline-flex items-center px-1.5 py-0.5 border border-[#5EEAD4]/50 rounded-sm bg-[#5EEAD4]/10" data-testid={`payment-method-chip-${o.order_number}`}>
                      <span className="pg-mono text-[9px] uppercase tracking-[0.12em] text-[#5EEAD4] font-semibold">
                        CARD{o.globalpay_card_last4 ? ` •${o.globalpay_card_last4}` : ""}
                      </span>
                    </div>
                  ) : (
                    <div className="mt-1 inline-flex items-center px-1.5 py-0.5 border border-[#A6FF00]/40 rounded-sm bg-[#A6FF00]/10" data-testid={`payment-method-chip-${o.order_number}`}>
                      <span className="pg-mono text-[9px] uppercase tracking-[0.12em] text-[#A6FF00] font-semibold">BANK</span>
                    </div>
                  )}
                </div>
                <div>
                  <div className="pg-micro mb-1">Status</div>
                  <span className="pg-badge-cyan">{o.status}</span>
                  {["shipped","dispatched","delivered"].includes(o.status) && o.shipping_email_sent === false && (
                    <div
                      className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#FF8A5B] rounded-sm bg-[#FF8A5B]/15 animate-pulse"
                      title="Dispatch email failed to send — click row to expand and resend"
                      data-testid={`dispatch-email-failed-${o.order_number}`}
                    >
                      <span className="pg-mono text-[9px] uppercase tracking-[0.12em] text-[#FF8A5B] font-semibold">
                        ⚠ Email Failed
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  <div className="pg-micro mb-1">Same-Day</div>
                  <div className={o.same_day_eligible ? "text-[#A6FF00] text-xs" : "text-[#9CA8B8] text-xs"}>
                    {o.same_day_eligible ? "Yes" : "No"}
                  </div>
                </div>
              </div>
            </button>

            {expanded === o.id && (
              <div className="mt-5 pt-5 border-t border-[#00C2D6]/20 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <div className="pg-micro mb-2">Items</div>
                  <div className="space-y-1">
                    {o.items.map((i) => (
                      <div key={i.sku} className="flex justify-between text-sm">
                        <span className="text-[#F7FAFC]">{i.name} {i.strength} × {i.quantity}</span>
                        <span className="text-[#9CA8B8] pg-mono">£{(i.price * i.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  {o.discount_code && (
                    <div
                      className="mt-2 flex justify-between text-sm border-t border-[#A6FF00]/20 pt-2"
                      data-testid={`order-discount-line-${o.order_number}`}
                    >
                      <span className="text-[#A6FF00]">
                        Discount code <span className="pg-mono font-semibold">{o.discount_code}</span>
                      </span>
                      <span className="text-[#A6FF00] pg-mono">−£{Number(o.discount || 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="mt-4 pg-micro mb-2">Shipping</div>
                  <div className="text-sm text-[#F7FAFC]">
                    {o.shipping.full_name}<br />
                    {o.shipping.address_line1}<br />
                    {o.shipping.address_line2 && <>{o.shipping.address_line2}<br /></>}
                    {o.shipping.city}, {o.shipping.postcode}<br />
                    {o.shipping.email} · {o.shipping.phone || "—"}
                  </div>
                  {o.shipping.date_of_birth && (
                    <div className="mt-3 text-[11px] text-[#9CA8B8] flex items-center gap-2" data-testid={`order-dob-${o.order_number}`}>
                      <span className="pg-mono uppercase tracking-[0.18em] text-[#5EEAD4]">DOB</span>
                      <span className="text-[#F7FAFC]">{(() => {
                        try {
                          // Render as DD/MM/YYYY UK format for clarity, then age in years
                          const [y, m, d] = o.shipping.date_of_birth.split("-");
                          const dob = new Date(Number(y), Number(m) - 1, Number(d));
                          const ms = Date.now() - dob.getTime();
                          const age = Math.floor(ms / (365.25 * 24 * 60 * 60 * 1000));
                          return `${d}/${m}/${y} · age ${age} at order`;
                        } catch { return o.shipping.date_of_birth; }
                      })()}</span>
                    </div>
                  )}
                  {o.fena_reference && (
                    <>
                      <div className="mt-4 pg-micro mb-1">Fena Reference</div>
                      <div className="pg-mono text-xs text-[#00C2D6]">{o.fena_reference}</div>
                    </>
                  )}
                  {o.payment_method === "GlobalPay Card" && o.globalpay_transaction_ref && (
                    <>
                      <div className="mt-4 pg-micro mb-1">Card Payment Ref (GlobalPay)</div>
                      <div className="pg-mono text-xs text-[#5EEAD4]">{o.globalpay_transaction_ref}{o.globalpay_card_last4 ? ` · •••• ${o.globalpay_card_last4}` : ""}</div>
                    </>
                  )}

                  {/* Timeline — all order timestamps in one block, UK local
                      time, so admin can quickly see when each event happened
                      (placed → paid → shipped → delivered). */}
                  <div className="mt-4 pg-micro mb-2">Timeline</div>
                  <div className="text-[11px] text-[#9CA8B8] space-y-0.5 pg-mono" data-testid={`order-timeline-${o.order_number}`}>
                    <div><span className="text-[#9CA8B8] inline-block w-20">Placed</span> <span className="text-[#F7FAFC]">{formatUkDateTime(o.created_at)}</span></div>
                    {o.payment_timestamp && (
                      <div><span className="text-[#9CA8B8] inline-block w-20">Paid</span> <span className="text-[#A6FF00]">{formatUkDateTime(o.payment_timestamp)}</span></div>
                    )}
                    {o.updated_at && o.updated_at !== o.created_at && (
                      <div><span className="text-[#9CA8B8] inline-block w-20">Updated</span> <span className="text-[#F7FAFC]">{formatUkDateTime(o.updated_at)}</span></div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="pg-micro mb-2">Update Status</div>
                    <select
                      value={o.status}
                      onChange={(e) => update(o.id, { status: e.target.value })}
                      className="pg-input text-sm"
                      data-testid={`status-${o.order_number}`}
                    >
                      {statuses.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                    </select>
                  </div>
                  <CustomerDetailsPanel
                    order={o}
                    onSave={(data) => update(o.id, data)}
                    onResend={() => resendConfirmation(o)}
                    onResendDispatch={() => resendDispatch(o)}
                    resending={resendBusy === o.id}
                  />
                  <DispatchPanel order={o} onSave={(data) => update(o.id, data)} />

                  {RECOVERABLE_STATUSES.has(o.payment_status) && o.shipping?.email && (() => {
                    const em = (o.shipping.email || "").toLowerCase().trim();
                    const paidElsewhere = orders.find(
                      (p) => p.id !== o.id && p.payment_status === "paid" &&
                        (p.shipping?.email || "").toLowerCase().trim() === em && (p.created_at || "") >= (o.created_at || "")
                    );
                    return (
                    <div className="border border-[#A6FF00]/30 rounded-sm p-3 bg-[#A6FF00]/[0.04]">
                      <div className="pg-mono text-[10px] uppercase tracking-[0.2em] text-[#A6FF00] mb-2">
                        {o.payment_status === "failed" ? "// Payment failed — chasing customer" : "// Payment incomplete — recoverable"}
                      </div>
                      {o.payment_status === "failed" && !paidElsewhere && (
                        <div className="text-[11px] mb-2" data-testid={`recovery-failed-nudge-${o.order_number}`}>
                          {o.recovery_failed_sent_at ? (
                            <span className="text-[#5EEAD4]">
                              ✓ "Payment didn't go through" email sent {fmtStamp(o.recovery_failed_sent_at)}{o.recovery_failed_ok === false ? " — delivery failed" : ""}
                            </span>
                          ) : (
                            <span className="text-[#9CA8B8]">○ "Payment didn't go through" email goes automatically 15 min after the failure</span>
                          )}
                        </div>
                      )}
                      {paidElsewhere ? (
                        <div className="text-[11px] text-[#5EEAD4] mb-3" data-testid={`recovery-stopped-${o.order_number}`}>
                          ✓ Recovery emails stopped — customer completed {paidElsewhere.order_number} instead. No reminders will be sent for this order.
                        </div>
                      ) : (
                      <>
                      {o.recovery_stopped && (
                        <div className="text-[11px] text-[#FF8A5B] mb-2" data-testid={`recovery-manual-stop-${o.order_number}`}>
                          ⏸ Automatic reminders switched off {fmtStamp(o.recovery_stopped_at)}{o.recovery_stopped_by ? ` by ${o.recovery_stopped_by}` : ""}
                          {o.recovery_stopped_reason ? ` — ${o.recovery_stopped_reason}` : ""}. Nothing more goes out unless you send it.
                        </div>
                      )}
                      <div className="text-[11px] text-[#9CA8B8] mb-3">
                        {o.payment_status === "failed" ? "Further follow-ups (manual):" : "Reminders sent:"}&nbsp;
                        <span className={o.recovery_first_sent_at ? "text-[#5EEAD4]" : "text-[#9CA8B8]"}>
                          {o.recovery_first_sent_at ? "✓" : "○"} 1st (15 min)
                        </span>
                        &nbsp;·&nbsp;
                        <span className={o.recovery_second_sent_at ? "text-[#5EEAD4]" : "text-[#9CA8B8]"}>
                          {o.recovery_second_sent_at ? "✓" : "○"} 2nd (6 h)
                        </span>
                        &nbsp;·&nbsp;
                        <span className={o.recovery_final_sent_at ? "text-[#5EEAD4]" : "text-[#9CA8B8]"}>
                          {o.recovery_final_sent_at ? "✓" : "○"} 3rd + code
                        </span>
                      </div>
                      </>
                      )}
                      {!paidElsewhere && (
                      <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); sendPaymentReminder(o); }}
                        disabled={reminderBusy === o.id || o.recovery_final_sent_at}
                        className="flex-1 text-[10px] font-bold tracking-[0.18em] uppercase rounded-sm py-2 px-3 border border-[#A6FF00]/35 text-[#A6FF00] hover:bg-[#A6FF00]/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`send-payment-reminder-${o.order_number}`}
                      >
                        <Mail size={12} />
                        {reminderBusy === o.id
                          ? "Sending…"
                          : o.recovery_final_sent_at
                            ? "All reminders sent"
                            : "Send payment-reminder now"}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleRecovery(o); }}
                        disabled={stopBusy === o.id}
                        className={`text-[10px] font-bold tracking-[0.18em] uppercase rounded-sm py-2 px-3 border transition-colors flex items-center justify-center gap-2 disabled:opacity-40 ${o.recovery_stopped ? "border-[#5EEAD4]/40 text-[#5EEAD4] hover:bg-[#5EEAD4]/10" : "border-[#FF8A5B]/40 text-[#FF8A5B] hover:bg-[#FF8A5B]/10"}`}
                        data-testid={`toggle-recovery-${o.order_number}`}
                        title={o.recovery_stopped ? "Switch the automatic reminder sequence back on" : "Stop the automatic reminder sequence (e.g. you're following up personally)"}
                      >
                        {stopBusy === o.id ? "…" : o.recovery_stopped ? "Resume auto" : "Stop auto reminders"}
                      </button>
                      </div>
                      )}

                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); markAsPaid(o); }}
                        disabled={markPaidBusy === o.id}
                        className="w-full mt-2 text-[10px] font-bold tracking-[0.18em] uppercase rounded-sm py-2 px-3 border border-[#5EEAD4]/50 text-[#5EEAD4] hover:bg-[#5EEAD4]/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`mark-paid-manual-${o.order_number}`}
                        title="Use after a customer has paid by bank transfer — fires email + Meta CAPI"
                      >
                        <CheckCircle2 size={12} />
                        {markPaidBusy === o.id ? "Marking…" : "Mark as paid (bank transfer)"}
                      </button>
                      <div className="mt-1.5 text-[9px] text-[#9CA8B8] tracking-wide">
                        Use only after the £{Number(o.total).toFixed(2)} transfer has landed in Tide.
                      </div>
                    </div>
                    );
                  })()}

                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); remove(o); }}
                    className="w-full text-[10px] font-bold tracking-[0.18em] uppercase rounded-sm py-2 px-3 border border-[#EF4444]/35 text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors flex items-center justify-center gap-2"
                    data-testid={`delete-order-${o.order_number}`}
                  >
                    <Trash2 size={12} /> Delete this order
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        {orders.length === 0 && <div className="text-[#9CA8B8] text-center py-12">No orders found</div>}
        {!search && orders.length >= 500 && !allLoaded && (
          <div className="text-center py-4">
            <button
              data-testid="load-older-orders-btn"
              onClick={loadOlder}
              disabled={loadingOlder}
              className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider border border-[#00C2D6]/40 text-[#00C2D6] rounded-sm hover:bg-[#00C2D6]/10 disabled:opacity-50"
            >
              {loadingOlder ? "Loading…" : `Load older orders (${orders.length} shown)`}
            </button>
          </div>
        )}
      </div>

      {cleanupPreview !== null && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="cleanup-modal">
          <div className="bg-[#0F172A] border border-[#FF8A5B]/40 rounded-sm w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1E2E44]">
              <div className="flex items-center gap-2">
                <AlertTriangle size={16} className="text-[#FF8A5B]" />
                <h2 className="text-[#F7FAFC] text-lg pg-heading" style={{ fontFamily: "'Rajdhani'" }}>
                  Clean up test orders
                </h2>
              </div>
              <button onClick={() => setCleanupPreview(null)} className="text-[#9CA8B8] hover:text-[#F7FAFC]" data-testid="close-cleanup">
                <X size={16} />
              </button>
            </div>

            <div className="px-5 py-4 overflow-y-auto">
              <p className="text-[12px] text-[#9CA8B8] mb-3 leading-relaxed">
                These orders match the test-order rules (unpaid + test email / test name / £0.50 SKU /
                peptigenlabs.co.uk staff email).{" "}
                <strong className="text-[#F7FAFC]">Paid orders and real customers are never matched.</strong>
              </p>

              <div className="mb-3 px-3 py-2 border border-[#1E2E44] rounded-sm bg-[#0B1220] text-[11px] text-[#9CA8B8]">
                <span className="pg-mono uppercase tracking-[0.18em] text-[#FF8A5B] text-[9px]">// total to delete</span>
                <span className="text-[#F7FAFC] text-xl font-bold ml-3">{cleanupPreview.count}</span>
              </div>

              {cleanupPreview.count === 0 ? (
                <div className="text-center py-8 text-[#9CA8B8] text-sm">
                  Nothing to clean up — no test orders found.
                </div>
              ) : (
                <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                  {cleanupPreview.candidates.map((c) => (
                    <div key={c.id} className="px-3 py-2 border border-[#1E2E44] rounded-sm bg-[#0B1220] text-[11px]" data-testid={`cleanup-candidate-${c.order_number}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="pg-mono text-[#F7FAFC] truncate">{c.order_number}</div>
                          <div className="text-[#9CA8B8] truncate">{c.name} · {c.email}</div>
                          <div className="text-[#5EEAD4] text-[10px] pg-mono uppercase tracking-[0.18em] mt-0.5">
                            // {c.reason}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[#A6FF00] pg-mono">£{Number(c.total).toFixed(2)}</div>
                          <div className="text-[#9CA8B8] text-[10px]">{c.status}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t border-[#1E2E44] flex justify-end gap-2">
              <button onClick={() => setCleanupPreview(null)} className="pg-btn-ghost text-xs" data-testid="cancel-cleanup">
                Cancel
              </button>
              <button
                onClick={confirmCleanup}
                disabled={cleanupBusy || cleanupPreview.count === 0}
                className="text-[10px] font-bold tracking-[0.18em] uppercase rounded-sm py-2 px-4 border border-[#EF4444]/45 text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors disabled:opacity-40 disabled:hover:bg-transparent flex items-center gap-2"
                data-testid="confirm-cleanup"
              >
                <Trash2 size={12} /> Delete {cleanupPreview.count} order{cleanupPreview.count === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DispatchPanel({ order, onSave }) {
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [courier, setCourier] = useState(order.courier || "Royal Mail Tracked 24");
  const [saving, setSaving] = useState(false);
  const dispatched = order.status === "shipped" || order.status === "dispatched";

  // Resync if the underlying order data refreshes
  useEffect(() => {
    setTracking(order.tracking_number || "");
    setCourier(order.courier || "Royal Mail Tracked 24");
  }, [order.id, order.tracking_number, order.courier]);

  const markDispatched = async () => {
    if (!tracking.trim()) {
      toast.error("Enter a tracking number first");
      return;
    }
    if (!window.confirm(
      `Send the dispatch email to ${order.shipping?.email} now?\n\n` +
      `Tracking: ${tracking}\nCourier: ${courier || "Royal Mail Tracked 24"}\n\n` +
      `This cannot be undone — only confirm if the parcel is genuinely dispatched.`
    )) return;
    setSaving(true);
    try {
      await onSave({
        tracking_number: tracking.trim(),
        courier: courier.trim() || "Royal Mail Tracked 24",
        status: "shipped",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateTrackingOnly = async () => {
    setSaving(true);
    try {
      await onSave({
        tracking_number: tracking.trim(),
        courier: courier.trim(),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-[#1E2E44] rounded-sm p-3 space-y-3 bg-[#0B1220]">
      <div className="pg-micro">// Dispatch &amp; tracking</div>

      <div>
        <label className="pg-micro mb-1 block">Tracking Number</label>
        <input
          value={tracking}
          onChange={(e) => setTracking(e.target.value)}
          placeholder="e.g. RM123456789GB"
          className="pg-input text-sm"
          data-testid={`tracking-${order.order_number}`}
        />
      </div>

      <div>
        <label className="pg-micro mb-1 block">Courier</label>
        <input
          value={courier}
          onChange={(e) => setCourier(e.target.value)}
          placeholder="Royal Mail Tracked 24"
          className="pg-input text-sm"
          data-testid={`courier-${order.order_number}`}
        />
      </div>

      {dispatched ? (
        <div className="text-[11px] text-[#A6FF00] flex items-center gap-2 px-2 py-2 border border-[#A6FF00]/30 rounded-sm bg-[#A6FF00]/[0.06]">
          <span className="pg-mono uppercase tracking-[0.18em]">Dispatched</span>
          <span className="text-[#9CA8B8]">— customer notified by email</span>
        </div>
      ) : (
        <div className="space-y-2">
          <button
            type="button"
            onClick={markDispatched}
            disabled={saving || !tracking.trim()}
            data-testid={`mark-dispatched-${order.order_number}`}
            className="w-full text-[10px] font-extrabold tracking-[0.18em] uppercase rounded-sm py-2.5 px-3"
            style={{
              background: tracking.trim() ? "#A6FF00" : "#2C3E5A",
              color: tracking.trim() ? "#0B1220" : "#9CA8B8",
              border: 0,
              cursor: tracking.trim() && !saving ? "pointer" : "not-allowed",
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? "Sending..." : "Mark dispatched & email customer"}
          </button>
          {(tracking !== (order.tracking_number || "") || courier !== (order.courier || "Royal Mail Tracked 24")) && (
            <button
              type="button"
              onClick={updateTrackingOnly}
              disabled={saving}
              className="w-full pg-btn-ghost text-[10px] tracking-[0.18em] uppercase"
              data-testid={`save-tracking-${order.order_number}`}
            >
              Save without sending email
            </button>
          )}
        </div>
      )}
    </div>
  );
}


function CustomerDetailsPanel({ order, onSave, onResend, onResendDispatch, resending }) {
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState(order.shipping?.email || "");
  const [fullName, setFullName] = useState(order.shipping?.full_name || "");
  const [phone, setPhone] = useState(order.shipping?.phone || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEmail(order.shipping?.email || "");
    setFullName(order.shipping?.full_name || "");
    setPhone(order.shipping?.phone || "");
  }, [order.id, order.shipping?.email, order.shipping?.full_name, order.shipping?.phone]);

  const dirty =
    email.trim().toLowerCase() !== (order.shipping?.email || "").toLowerCase() ||
    fullName.trim() !== (order.shipping?.full_name || "") ||
    phone.trim() !== (order.shipping?.phone || "");

  const save = async () => {
    if (!email.trim()) {
      toast.error("Email is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      if (email.trim().toLowerCase() !== (order.shipping?.email || "").toLowerCase()) {
        payload.shipping_email = email.trim();
      }
      if (fullName.trim() !== (order.shipping?.full_name || "")) {
        payload.shipping_full_name = fullName.trim();
      }
      if (phone.trim() !== (order.shipping?.phone || "")) {
        payload.shipping_phone = phone.trim();
      }
      await onSave(payload);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-[#1E2E44] rounded-sm p-3 space-y-3 bg-[#0B1220]" data-testid={`customer-panel-${order.order_number}`}>
      <div className="flex items-center justify-between">
        <div className="pg-micro flex items-center gap-1.5"><UserCog size={11} /> Customer details</div>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-[9px] pg-mono uppercase tracking-[0.18em] text-[#00C2D6] hover:text-[#5EEAD4]"
            data-testid={`edit-customer-${order.order_number}`}
          >
            Edit
          </button>
        )}
      </div>

      {editing ? (
        <>
          <div>
            <label className="pg-micro mb-1 block">Email *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pg-input text-sm"
              data-testid={`customer-email-${order.order_number}`}
              placeholder="customer@example.com"
            />
          </div>
          <div>
            <label className="pg-micro mb-1 block">Full name</label>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="pg-input text-sm"
              data-testid={`customer-name-${order.order_number}`}
            />
          </div>
          <div>
            <label className="pg-micro mb-1 block">Phone</label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="pg-input text-sm"
              data-testid={`customer-phone-${order.order_number}`}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              className="flex-1 text-[10px] font-extrabold tracking-[0.18em] uppercase rounded-sm py-2"
              style={{
                background: dirty ? "#A6FF00" : "#2C3E5A",
                color: dirty ? "#0B1220" : "#9CA8B8",
                border: 0,
                cursor: dirty && !saving ? "pointer" : "not-allowed",
                opacity: saving ? 0.6 : 1,
              }}
              data-testid={`save-customer-${order.order_number}`}
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setEmail(order.shipping?.email || "");
                setFullName(order.shipping?.full_name || "");
                setPhone(order.shipping?.phone || "");
              }}
              className="pg-btn-ghost text-[10px] tracking-[0.18em] uppercase px-3"
              data-testid={`cancel-customer-${order.order_number}`}
            >
              Cancel
            </button>
          </div>
          <p className="text-[10px] text-[#9CA8B8] leading-relaxed">
            Saving updates the order record. To send the customer a fresh receipt, click &quot;Resend confirmation&quot; after saving.
          </p>
        </>
      ) : (
        <div className="text-[12px] text-[#F7FAFC] space-y-0.5">
          <div className="text-[#5EEAD4]">{order.shipping?.email || "— no email —"}</div>
          <div className="text-[#9CA8B8]">{order.shipping?.full_name}{order.shipping?.phone ? ` · ${order.shipping.phone}` : ""}</div>
        </div>
      )}

      <button
        type="button"
        onClick={onResend}
        disabled={resending || !order.shipping?.email}
        className="w-full text-[10px] font-bold tracking-[0.18em] uppercase rounded-sm py-2 px-3 border border-[#00C2D6]/40 text-[#00C2D6] hover:bg-[#00C2D6]/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid={`resend-confirmation-${order.order_number}`}
      >
        <Send size={12} />
        {resending ? "Sending…" : "Resend order confirmation"}
      </button>

      {/* Dispatch resend — only meaningful once the order has actually
          shipped. Lights up red when Resend dropped the original send
          (shipping_email_sent=false) so the operator can't miss it. */}
      {["shipped", "dispatched", "delivered"].includes(order.status) && (
        <button
          type="button"
          onClick={onResendDispatch}
          disabled={resending || !order.shipping?.email}
          className={`w-full text-[10px] font-bold tracking-[0.18em] uppercase rounded-sm py-2 px-3 border transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed ${
            order.shipping_email_sent === false
              ? "border-[#FF8A5B] text-[#FF8A5B] bg-[#FF8A5B]/10 hover:bg-[#FF8A5B]/20 animate-pulse"
              : "border-[#5EEAD4]/40 text-[#5EEAD4] hover:bg-[#5EEAD4]/10"
          }`}
          data-testid={`resend-dispatch-${order.order_number}`}
        >
          <Send size={12} />
          {resending
            ? "Sending…"
            : order.shipping_email_sent === false
              ? "⚠ Dispatch email failed — resend now"
              : "Resend dispatch / tracking email"}
        </button>
      )}
    </div>
  );
}


function ReturningChip({ paid, total }) {
  // Surface a quick signal on each order row when the customer has placed
  // more than one order with us — based on shipping.email match.
  // ``paid`` = count of paid orders by same email, ``total`` = count
  // including unpaid. We prefer to show the paid count because that's the
  // commercially meaningful number, but fall back to total if no paid.
  if (paid >= 5) {
    return (
      <span
        className="text-[9px] pg-mono uppercase tracking-[0.18em] bg-[#A6FF00]/15 text-[#A6FF00] border border-[#A6FF00]/35 rounded-sm px-1.5 py-0.5"
        title={`Customer has ${paid} paid orders (${total} total)`}
        data-testid="returning-chip-vip"
      >
        VIP · {paid}×
      </span>
    );
  }
  if (paid >= 2) {
    return (
      <span
        className="text-[9px] pg-mono uppercase tracking-[0.18em] bg-[#5EEAD4]/15 text-[#5EEAD4] border border-[#5EEAD4]/35 rounded-sm px-1.5 py-0.5"
        title={`Customer has ${paid} paid orders (${total} total)`}
        data-testid="returning-chip"
      >
        Returning · {paid}×
      </span>
    );
  }
  if (paid === 1 && total > 1) {
    // 1 paid but also some pending/unpaid → trying again
    return (
      <span
        className="text-[9px] pg-mono uppercase tracking-[0.18em] bg-[#00C2D6]/15 text-[#00C2D6] border border-[#00C2D6]/35 rounded-sm px-1.5 py-0.5"
        title={`Customer has 1 paid + ${total - 1} other order(s)`}
        data-testid="returning-chip-mixed"
      >
        1 paid · {total - 1} other
      </span>
    );
  }
  return null;
}
